import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Popup } from 'semantic-ui-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './userOverviewV101.css';

const COLORS = [
  '#5282d8',
  '#23a69a',
  '#ac86cf',
  '#da9a52',
  '#da758c',
  '#7b98a6',
  '#8ca855',
  '#907abd',
  '#cc8269',
  '#57a6c7',
];
const compact = (value) =>
  new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);
const number = (value, digits = 0) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: digits }).format(
    Number(value) || 0
  );
const hourLabel = (hour, full = false) =>
  new Date(Number(hour) * 1000).toLocaleString([], {
    ...(full ? { month: 'short', day: 'numeric' } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

export const OverviewTooltipV101 = ({ active, payload, label, digits = 0 }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className='overview-tooltip-v101'>
      <time>{hourLabel(label, true)}</time>
      {payload.map((item, index) => (
        <div key={index}>
          <span
            className='overview-swatch-v101'
            style={{ background: item.color }}
          />
          <span>{item.name}</span>
          <strong>{number(item.value, digits)}</strong>
        </div>
      ))}
    </div>
  );
};

const OverviewChartsV101 = ({ overview, t }) => {
  const { i18n } = useTranslation();
  const zh = (i18n?.resolvedLanguage || i18n?.language || 'en').startsWith(
    'zh'
  );
  const [view, setView] = useState('area');
  const id = useId().replace(/:/g, '');
  const text = zh
    ? {
        title: '模型 Token 用量',
        unit: 'Token',
        area: '面积图',
        bar: '柱状图',
        empty: '最近 24 小时暂无用量',
        period: '最近 24 小时',
      }
    : {
        title: 'Token usage by model',
        unit: 'Tokens',
        area: 'Area chart',
        bar: 'Bar chart',
        empty: 'No usage in the last 24 hours',
        period: 'Last 24 hours',
      };
  const axis = {
    dataKey: 'hour',
    axisLine: false,
    tickLine: false,
    tick: { fill: 'var(--ui-text-secondary)', fontSize: 11 },
    tickMargin: 12,
    minTickGap: 32,
    tickFormatter: (hour) => hourLabel(hour),
  };
  const Chart = view === 'area' ? AreaChart : BarChart;
  return (
    <div className='overview-charts-v101'>
      <section className='overview-trends-v101' aria-label={text.period}>
        {[
          { key: 'requests', color: COLORS[0] },
          { key: 'quota', color: COLORS[1] },
          { key: 'tokens', color: COLORS[2] },
        ].map(({ key, color }) => (
          <section
            key={key}
            className='overview-trend-v101'
            aria-label={t(`dashboard.charts.${key}.title`)}
          >
            <h2>{t(`dashboard.charts.${key}.title`)}</h2>
            <div className='overview-trend-plot-v101'>
              <ResponsiveContainer width='100%' height='100%'>
                <AreaChart
                  data={overview.trend}
                  margin={{ top: 12, right: 8, left: 8, bottom: 0 }}
                  accessibilityLayer
                >
                  <defs>
                    <linearGradient
                      id={`${id}-${key}`}
                      x1='0'
                      y1='0'
                      x2='0'
                      y2='1'
                    >
                      <stop offset='0%' stopColor={color} stopOpacity={0.18} />
                      <stop
                        offset='100%'
                        stopColor={color}
                        stopOpacity={0.015}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke='var(--ui-border)'
                    strokeOpacity={0.45}
                  />
                  <XAxis {...axis} />
                  <YAxis hide domain={[0, 'auto']} tickCount={3} />
                  <Tooltip
                    content={
                      <OverviewTooltipV101 digits={key === 'quota' ? 4 : 0} />
                    }
                    cursor={{
                      stroke: color,
                      strokeOpacity: 0.35,
                      strokeDasharray: '3 4',
                    }}
                  />
                  <Area
                    type='monotone'
                    dataKey={key}
                    name={t(`dashboard.charts.${key}.tooltip`)}
                    stroke={color}
                    strokeWidth={2}
                    fill={`url(#${id}-${key})`}
                    dot={false}
                    activeDot={{
                      r: 4,
                      stroke: 'var(--ui-surface)',
                      strokeWidth: 2,
                    }}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>
        ))}
      </section>
      <section className='overview-models-v101' aria-label={text.title}>
        <header className='overview-chart-header-v101'>
          <div>
            <h2>{text.title}</h2>
            <span>{text.period}</span>
          </div>
          <div
            className='overview-chart-modes-v101'
            role='group'
            aria-label={text.title}
          >
            {['area', 'bar'].map((mode) => (
              <Popup
                key={mode}
                content={text[mode]}
                on={['hover', 'focus']}
                trigger={
                  <Button
                    icon
                    basic
                    toggle
                    active={view === mode}
                    aria-label={text[mode]}
                    onClick={() => setView(mode)}
                  >
                    <Icon name={mode === 'area' ? 'area chart' : 'bar chart'} />
                  </Button>
                }
              />
            ))}
          </div>
        </header>
        {overview.models.length ? (
          <>
            <div className='overview-model-plot-v101' data-view={view}>
              <ResponsiveContainer width='100%' height='100%'>
                <Chart
                  data={overview.trend}
                  margin={{ top: 18, right: 10, left: 0, bottom: 0 }}
                  barCategoryGap='35%'
                  accessibilityLayer
                >
                  <CartesianGrid
                    vertical={false}
                    stroke='var(--ui-border)'
                    strokeOpacity={0.45}
                  />
                  <XAxis {...axis} />
                  <YAxis
                    width={46}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--ui-text-secondary)', fontSize: 11 }}
                    tickFormatter={compact}
                    tickCount={5}
                  />
                  <Tooltip
                    content={<OverviewTooltipV101 />}
                    cursor={
                      view === 'area'
                        ? {
                            stroke: 'var(--ui-text-secondary)',
                            strokeDasharray: '3 4',
                          }
                        : {
                            fill: 'var(--ui-text-secondary)',
                            fillOpacity: 0.06,
                          }
                    }
                  />
                  {overview.models.map((model, index) => {
                    const color = COLORS[index % COLORS.length];
                    const props = {
                      name: model,
                      dataKey: (row) => row[model] || 0,
                      stackId: 'tokens',
                      isAnimationActive: false,
                    };
                    return view === 'area' ? (
                      <Area
                        key={model}
                        {...props}
                        type='monotone'
                        stroke={color}
                        strokeWidth={1.6}
                        fill={color}
                        fillOpacity={0.18}
                        dot={false}
                        activeDot={{ r: 3 }}
                      />
                    ) : (
                      <Bar
                        key={model}
                        {...props}
                        fill={color}
                        fillOpacity={0.85}
                        maxBarSize={24}
                        radius={
                          index === overview.models.length - 1
                            ? [4, 4, 0, 0]
                            : [0, 0, 0, 0]
                        }
                      />
                    );
                  })}
                </Chart>
              </ResponsiveContainer>
            </div>
            <ul className='overview-legend-v101' aria-label={text.unit}>
              {overview.models.map((model, index) => (
                <li key={model}>
                  <span
                    className='overview-swatch-v101'
                    style={{ background: COLORS[index % COLORS.length] }}
                  />
                  <span>{model}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className='overview-empty-v101' role='status'>
            <Icon name='chart line' aria-hidden='true' />
            <span>{text.empty}</span>
          </div>
        )}
      </section>
    </div>
  );
};

export default OverviewChartsV101;
